
import { useState, useEffect, useRef } from 'react';
import { setLoading, setSnackInfo } from '../redux/actions/appAction';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { connect, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import {
    Paper,
    Grid,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
    Box,
    Typography,
    Chip,
    CircularProgress,
    Divider,
} from '@material-ui/core';
import HttpService from '../services/httpService';

const StyledTableCell = withStyles((theme) => ({
    head: {
        backgroundColor: '#1C375D',
        color: theme.palette.common.white,
        fontWeight: 600,
        fontSize: 13,
        whiteSpace: 'nowrap',
        padding: '10px 14px',
    },
    body: {
        fontSize: 13,
        padding: '8px 14px',
    },
}))(TableCell);

const StyledTableRow = withStyles(() => ({
    root: {
        '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
        '&:hover':             { backgroundColor: '#eef4ff' },
    },
}))(TableRow);

// ── Component styles ──────────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(3),
        marginTop: 20,
    },
    summaryCard: {
        padding: theme.spacing(1.5, 2.5),
        border: '1px solid #e0e0e0',
        borderRadius: 8,
        textAlign: 'center',
        minWidth: 150,
    },
    summaryLabel: { fontSize: 12, color: '#888', marginBottom: 4 },
    summaryValue: { fontSize: 22, fontWeight: 700 },
    errorRow: { backgroundColor: '#fff5f5' },
    csv: {
        display: 'flex',
        gap: 10,
        marginBottom: 16,
    },
    popupTable: {
        marginTop: 8,
    },
    noData: {
        padding: theme.spacing(3),
        textAlign: 'center',
        color: '#aaa',
        fontStyle: 'italic',
    },
    loaderBox: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 150,
    },
    linkStyle: {
        color: '#1C375D',
        textDecoration: 'underline',
        textAlign: 'center',
    },
    missingRowHighlight: {
        backgroundColor: '#fff5f5 !important',
        '&:hover': { backgroundColor: '#ffe8e8 !important' },
    },
    driftedRowHighlight: {
        backgroundColor: '#fffbf0 !important',
        '&:hover': { backgroundColor: '#fff0d0 !important' },
    },
}));

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (d) => (d ? moment(d).format('DD-MM-YYYY') : '-');

const TabPanel = ({ children, value, index }) =>
    value === index ? <Box>{children}</Box> : null;

// ── Collect all diff lead IDs from a diffData object ─────────────────────────
const collectLeadIds = (diffData) => {
    if (!diffData) return [];
    const fromMissing = (diffData.missingInJourney || []).map((r) => r.leadId);
    const fromDrifted = (diffData.dateDrifted       || []).map((r) => r.leadId);
    // Deduplicate (a lead shouldn't be in both, but just in case)
    return [...new Set([...fromMissing, ...fromDrifted])];
};


// ─────────────────────────────────────────────────────────────────────────────
const SalesOverallComparison = (props) => {
    const classes = useStyles();
    const dispatch = useDispatch();

    const [data, setData]                   = useState([]);
    const [summary, setSummary]             = useState({});
    const [open, setOpen]                   = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [diffData, setDiffData]           = useState(null);
    const [diffLoading, setDiffLoading]     = useState(false);
    const [activeTab, setActiveTab]         = useState(0);
    const [syncLoading, setSyncLoading]     = useState(false);
    const [loadingData , setLoadingData]            = useState(false)

    const missingCsvRef = useRef();
    const driftedCsvRef = useRef();

    const fetchData = async () => {
        try {
            props.setLoading(true);
            setLoadingData(true)
            const res = await HttpService.compareMonthwiseCount();
            setData(res.data.comparison || []);
            setSummary(res.data.summary  || {});
        } catch (err) {
            setLoadingData(false)
            props.setSnackInfo(err?.response?.data ?? 'Error fetching data', 'error');
        } finally {
            props.setLoading(false);
            setLoadingData(false)
        }
    };

    useEffect(() => { fetchData(); }, []);

    const fetchDiffForRow = async (row) => {
        const res = await HttpService.diffLeadsForMonth({ month: row.month, year: row.year });
        return res.data;
    };

    const handleOpenPopup = async (row) => {
        setSelectedMonth(row);
        setDiffData(null);
        setActiveTab(0);
        setOpen(true);
        try {
            setDiffLoading(true);
            const data = await fetchDiffForRow(row);
            setDiffData(data);
        } catch (err) {
            props.setSnackInfo(err?.response?.data ?? 'Error fetching diff details', 'error');
        } finally {
            setDiffLoading(false);
        }
    };

    const handleSync = async ({ leadIds = [], row = null } = {}) => {
        try {
            setSyncLoading(true);

            let ids = leadIds;

            if (!ids.length && row) {
                const fetched = await fetchDiffForRow(row);
                ids = collectLeadIds(fetched);
            }

            if (!ids.length) {
                props.setSnackInfo('No discrepancy leads found to sync.', 'warning');
                return;
            }

            const res = await HttpService.syncLeadJourneyFromLead({ leadIds: ids });

            if (res.status === 200) {
                props.setSnackInfo(
                    `Sync complete — ${res.data.totalJourneyUpdated} updated, ` +
                    `${res.data.totalJourneyCreated} created`,
                    'success'
                );
            }

            fetchData();
        } catch (err) {
            props.setSnackInfo(err?.response?.data ?? 'Sync failed', 'error');
        } finally {
            setSyncLoading(false);
        }
    };

    const handleClose = () => { setOpen(false); setDiffData(null); setSelectedMonth(null); };

    const missingCsvHeaders = [
        { label: 'Lead No',             key: 'lead_no' },
        { label: 'Unique Lead No',      key: 'uniqueLeadNumber' },
        { label: 'ERP Project No',      key: 'erpProjectNo' },
        { label: 'Lead Won Date',       key: 'leadWonDate' },
        { label: 'Current Stage',       key: 'currentStage' },
        { label: 'Token %',             key: 'tokenPercent' },
        { label: 'Sales User',          key: 'salesUserName' },
        { label: 'Asst. Sales Manager', key: 'salesAssistantManagerName' },
        { label: 'Sales Manager',       key: 'salesManagerName' },
        { label: 'Sales DGM',           key: 'salesDGMName' },
        { label: 'Reason',              key: 'reason' },
    ];
    const driftedCsvHeaders = [
        ...missingCsvHeaders,
        { label: 'Journey SalesWonDate', key: 'journeySalesWonDate' },
        { label: 'Journey Month',        key: 'journeyMonth' },
        { label: 'Journey Year',         key: 'journeyYear' },
    ];

    const missingCsvData = (diffData?.missingInJourney || []).map((r) => ({
        ...r,
        leadWonDate: fmt(r.leadWonDate),
    }));
    const driftedCsvData = (diffData?.dateDrifted || []).map((r) => ({
        ...r,
        leadWonDate:         fmt(r.leadWonDate),
        journeySalesWonDate: fmt(r.journeySalesWonDate),
    }));

    const MissingTable = ({ rows = [] }) => {
        if (!rows.length) return <div className={classes.noData}>No missing leads ✓</div>;
        return (
            <TableContainer component={Paper} className={classes.popupTable}>
                <Table stickyHeader aria-label="missing leads table">
                    <TableHead>
                        <StyledTableRow>
                            <StyledTableCell>Sl. No.</StyledTableCell>
                            <StyledTableCell>Project No.</StyledTableCell>
                            <StyledTableCell>Lead No.</StyledTableCell>
                            <StyledTableCell>Unique Lead No.</StyledTableCell>
                            <StyledTableCell>Lead Won Date</StyledTableCell>
                            <StyledTableCell>Current Stage</StyledTableCell>
                            <StyledTableCell>Token %</StyledTableCell>
                            <StyledTableCell>Sales User</StyledTableCell>
                            <StyledTableCell>Asst. Sales Manager</StyledTableCell>
                            <StyledTableCell>Sales Manager</StyledTableCell>
                            <StyledTableCell>Sales DGM</StyledTableCell>
                            <StyledTableCell>Reason</StyledTableCell>
                        </StyledTableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((lead, i) => (
                            <StyledTableRow key={lead.leadId} className={classes.missingRowHighlight}>
                                <StyledTableCell>{i + 1}</StyledTableCell>
                                <StyledTableCell style={{ textAlign: 'center' }}>
                                    {lead.erpProjectNo
                                        ? <Link to={`/project-logs/${lead.leadId}`} target="_blank" rel="noopener noreferrer" className={classes.linkStyle}>{lead.erpProjectNo}</Link>
                                        : 'NA'}
                                </StyledTableCell>
                                <StyledTableCell style={{ textAlign: 'center' }}>
                                    {lead.lead_no
                                        ? <Link to={`/project-logs/${lead.leadId}`} target="_blank" rel="noopener noreferrer" className={classes.linkStyle}>{lead.lead_no}</Link>
                                        : 'NA'}
                                </StyledTableCell>
                                <StyledTableCell>{lead.uniqueLeadNumber ?? '-'}</StyledTableCell>
                                <StyledTableCell>{fmt(lead.leadWonDate)}</StyledTableCell>
                                <StyledTableCell>{lead.currentStage ?? '-'}</StyledTableCell>
                                <StyledTableCell style={{ textAlign: 'center' }}>{lead.tokenPercent ?? '-'}</StyledTableCell>
                                <StyledTableCell>{lead.salesUserName             ?? '-'}</StyledTableCell>
                                <StyledTableCell>{lead.salesAssistantManagerName ?? '-'}</StyledTableCell>
                                <StyledTableCell>{lead.salesManagerName          ?? '-'}</StyledTableCell>
                                <StyledTableCell>{lead.salesDGMName              ?? '-'}</StyledTableCell>
                                <StyledTableCell style={{ color: '#c62828', fontSize: 12 }}>{lead.reason}</StyledTableCell>
                            </StyledTableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    const DriftedTable = ({ rows = [] }) => {
        if (!rows.length) return <div className={classes.noData}>No date-drifted leads ✓</div>;
        return (
            <TableContainer component={Paper} className={classes.popupTable}>
                <Table stickyHeader aria-label="drifted leads table">
                    <TableHead>
                        <StyledTableRow>
                            <StyledTableCell>Sl. No.</StyledTableCell>
                            <StyledTableCell>Project No.</StyledTableCell>
                            <StyledTableCell>Lead No.</StyledTableCell>
                            <StyledTableCell>Unique Lead No.</StyledTableCell>
                            <StyledTableCell>Lead Won Date (Lead)</StyledTableCell>
                            <StyledTableCell>SalesWonDate (Journey)</StyledTableCell>
                            <StyledTableCell>Current Stage</StyledTableCell>
                            <StyledTableCell>Token %</StyledTableCell>
                            <StyledTableCell>Sales User</StyledTableCell>
                            <StyledTableCell>Asst. Sales Manager</StyledTableCell>
                            <StyledTableCell>Sales Manager</StyledTableCell>
                            <StyledTableCell>Sales DGM</StyledTableCell>
                            <StyledTableCell>Reason</StyledTableCell>
                        </StyledTableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((lead, i) => (
                            <StyledTableRow key={lead.leadId} className={classes.driftedRowHighlight}>
                                <StyledTableCell>{i + 1}</StyledTableCell>
                                <StyledTableCell style={{ textAlign: 'center' }}>
                                    {lead.erpProjectNo
                                        ? <Link to={`/project-logs/${lead.leadId}`} target="_blank" rel="noopener noreferrer" className={classes.linkStyle}>{lead.erpProjectNo}</Link>
                                        : 'NA'}
                                </StyledTableCell>
                                <StyledTableCell style={{ textAlign: 'center' }}>
                                    {lead.lead_no
                                        ? <Link to={`/project-logs/${lead.leadId}`} target="_blank" rel="noopener noreferrer" className={classes.linkStyle}>{lead.lead_no}</Link>
                                        : 'NA'}
                                </StyledTableCell>
                                <StyledTableCell>{lead.uniqueLeadNumber ?? '-'}</StyledTableCell>
                                <StyledTableCell>{fmt(lead.leadWonDate)}</StyledTableCell>
                                <StyledTableCell style={{ color: '#e65100', fontWeight: 600 }}>
                                    {fmt(lead.journeySalesWonDate)}
                                </StyledTableCell>
                                <StyledTableCell>{lead.currentStage ?? '-'}</StyledTableCell>
                                <StyledTableCell style={{ textAlign: 'center' }}>{lead.tokenPercent ?? '-'}</StyledTableCell>
                                <StyledTableCell>{lead.salesUserName             ?? '-'}</StyledTableCell>
                                <StyledTableCell>{lead.salesAssistantManagerName ?? '-'}</StyledTableCell>
                                <StyledTableCell>{lead.salesManagerName          ?? '-'}</StyledTableCell>
                                <StyledTableCell>{lead.salesDGMName              ?? '-'}</StyledTableCell>
                                <StyledTableCell style={{ color: '#e65100', fontSize: 12 }}>{lead.reason}</StyledTableCell>
                            </StyledTableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    return (
        <Paper className={classes.paper}>
            <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 16 }}>
                Sales vs Journey Comparison
            </Typography>

            <Grid container spacing={2} style={{ marginBottom: 16 }}>
                {[
                    { label: 'Total Lead Count',    value: summary.totalLeadCount    ?? '—' },
                    { label: 'Total Journey Count', value: summary.totalJourneyCount ?? '—' },
                    { label: 'Difference',          value: summary.totalDifference   ?? '—', color: summary.totalDifference    > 0 ? '#d32f2f' : '#388e3c' },
                    { label: 'Mismatch Months',     value: summary.discrepancyMonthsCount ?? '—', color: summary.discrepancyMonthsCount > 0 ? '#f57c00' : '#388e3c' },
                ].map(({ label, value, color }) => (
                    <Grid item key={label}>
                        <div className={classes.summaryCard}>
                            <div className={classes.summaryLabel}>{label}</div>
                            <div className={classes.summaryValue} style={color ? { color } : {}}>{value}</div>
                        </div>
                    </Grid>
                ))}
            </Grid>

            <Divider />

            <TableContainer component={Paper} style={{ marginTop: 16 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <StyledTableRow>
                            <StyledTableCell>Month</StyledTableCell>
                            <StyledTableCell align="center">Lead Count</StyledTableCell>
                            <StyledTableCell align="center">Journey Count</StyledTableCell>
                            <StyledTableCell align="center">Difference</StyledTableCell>
                            <StyledTableCell align="center">Actions</StyledTableCell>
                        </StyledTableRow>
                    </TableHead>
                    <TableBody>
                        {
                            loadingData ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={5} align="center">
                                        <CircularProgress size={24} />
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : (
                                data.map((row, idx) => (
                                    <StyledTableRow
                                        key={idx}
                                        className={row.hasDiscrepancy ? classes.errorRow : ''}
                                    >
                                        <StyledTableCell>{row.monthName} {row.year}</StyledTableCell>
                                        <StyledTableCell align="center">{row.leadCount}</StyledTableCell>
                                        <StyledTableCell align="center">{row.journeyCount}</StyledTableCell>
                                        <StyledTableCell align="center">
                                            <Chip
                                                label={row.difference > 0 ? `+${row.difference}` : row.difference}
                                                size="small"
                                                style={{
                                                    backgroundColor: row.difference !== 0
                                                        ? (row.difference > 0 ? '#ffebee' : '#e8f5e9')
                                                        : '#f5f5f5',
                                                    color: row.difference !== 0
                                                        ? (row.difference > 0 ? '#c62828' : '#2e7d32')
                                                        : '#555',
                                                    fontWeight: 700,
                                                }}
                                            />
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            {row.hasDiscrepancy && (
                                                <>
                                                    <Button
                                                        variant="contained"
                                                        color="secondary"
                                                        size="small"
                                                        onClick={() => handleOpenPopup(row)}
                                                    >
                                                        View
                                                    </Button>

                                                    <Button
                                                        style={{ marginLeft: 8, backgroundColor: '#355025', color: '#FFF2EB' }}
                                                        variant="contained"
                                                        size="small"
                                                        disabled={syncLoading}
                                                        onClick={() => handleSync({ row })}
                                                    >
                                                        {syncLoading ? 'Syncing…' : 'Sync'}
                                                    </Button>
                                                </>
                                            )}
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))
                            )
                        }


                    </TableBody>
                </Table>
            </TableContainer>


            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xl" scroll="paper">
                <DialogTitle disableTypography style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" style={{ fontWeight: 700 }}>
                        Discrepancy Details — {selectedMonth?.monthName} {selectedMonth?.year}
                    </Typography>
                    {diffData && (
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                            <Chip label={`Lead Count: ${diffData.totalLeadsInMonth}`}  size="small" style={{ backgroundColor: '#e3f2fd', fontWeight: 600 }} />
                            <Chip label={`Missing: ${diffData.missingCount}`}           size="small" style={{ backgroundColor: '#ffebee', color: '#c62828', fontWeight: 600 }} />
                            <Chip label={`Date Drifted: ${diffData.dateDriftCount}`}   size="small" style={{ backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 600 }} />
                        </div>
                    )}
                </DialogTitle>

                <DialogContent dividers>
                    {diffLoading ? (
                        <div className={classes.loaderBox}><CircularProgress /></div>
                    ) : diffData ? (
                        <>
                            {/* CSV download buttons */}
                            <div className={classes.csv}>
                                <CSVLink
                                    headers={missingCsvHeaders}
                                    data={missingCsvData}
                                    filename={`missing_leads_${selectedMonth?.monthName}_${selectedMonth?.year}.csv`}
                                    ref={missingCsvRef}
                                />
                                <CSVLink
                                    headers={driftedCsvHeaders}
                                    data={driftedCsvData}
                                    filename={`drifted_leads_${selectedMonth?.monthName}_${selectedMonth?.year}.csv`}
                                    ref={driftedCsvRef}
                                />
                                <Button
                                    onClick={() => missingCsvRef.current?.link?.click()}
                                    style={{ backgroundColor: '#355025', color: '#FFF2EB' }}
                                    variant="contained"
                                    size="small"
                                >
                                    Export Missing ({diffData.missingCount})
                                </Button>
                                <Button
                                    onClick={() => driftedCsvRef.current?.link?.click()}
                                    style={{ backgroundColor: '#7B4A00', color: '#FFF2EB' }}
                                    variant="contained"
                                    size="small"
                                >
                                    Export Drifted ({diffData.dateDriftCount})
                                </Button>
                            </div>

                            <Tabs
                                value={activeTab}
                                onChange={(_, v) => setActiveTab(v)}
                                indicatorColor="primary"
                                textColor="primary"
                                style={{ marginBottom: 12 }}
                            >
                                <Tab label={
                                    <span>Missing in Journey &nbsp;
                                        <Chip label={diffData.missingCount} size="small"
                                            style={{ backgroundColor: diffData.missingCount > 0 ? '#ffcdd2' : '#c8e6c9', fontSize: 11, height: 18 }} />
                                    </span>
                                } />
                                <Tab label={
                                    <span>Date Drifted &nbsp;
                                        <Chip label={diffData.dateDriftCount} size="small"
                                            style={{ backgroundColor: diffData.dateDriftCount > 0 ? '#ffe0b2' : '#c8e6c9', fontSize: 11, height: 18 }} />
                                    </span>
                                } />
                            </Tabs>

                            <TabPanel value={activeTab} index={0}>
                                <Typography variant="body2" style={{ color: '#888', marginBottom: 8 }}>
                                    Leads that exist in the Lead model for this month but have <b>no LeadJourneyLog entry</b>.
                                </Typography>
                                <MissingTable rows={diffData.missingInJourney} />
                            </TabPanel>

                            <TabPanel value={activeTab} index={1}>
                                <Typography variant="body2" style={{ color: '#888', marginBottom: 8 }}>
                                    Leads whose LeadJourneyLog exists but <b>SalesWonDate falls in a different month</b> than leadWonDate.
                                </Typography>
                                <DriftedTable rows={diffData.dateDrifted} />
                            </TabPanel>
                        </>
                    ) : (
                        <div className={classes.noData}>No data available.</div>
                    )}
                </DialogContent>

                <DialogActions style={{ justifyContent: 'space-between', padding: '12px 24px' }}>
                    <div style={{ display: 'flex', gap: 10 }}>
                        {/* Popup sync: passes pre-collected IDs directly — no extra fetch needed */}
                        {diffData && collectLeadIds(diffData).length > 0 && (
                            <Button
                                style={{ backgroundColor: '#355025', color: '#FFF2EB' }}
                                variant="contained"
                                disabled={syncLoading}
                                onClick={() => handleSync({ leadIds: collectLeadIds(diffData) })}
                            >
                                {syncLoading
                                    ? 'Syncing…'
                                    : `Sync This Month (${collectLeadIds(diffData).length} leads)`}
                            </Button>
                        )}
                        <Button onClick={handleClose} variant="outlined">Close</Button>
                    </div>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default connect(null, { setLoading, setSnackInfo })(SalesOverallComparison);